import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import EntityList from '../../components/common/EntityList/EntityList';
import EditJobModal from '../../features/jobs/EditJobModal';
import AddJobModal from '../../features/jobs/AddJobModal';
import CloneJobModal from '../../features/jobs/CloneJobModal';
import LoadingBackdrop from '../../components/common/LoadingBackdrop/LoadingBackdrop';
import ConfirmationModal from '../../components/common/ConfirmationModal/ConfirmationModal';
import { jobService } from '../../services/apiService';
import useDebounce from '../../hooks/useDebounce';
import useJobStatusSocket, { SOCKET_STATE } from '../../hooks/useJobStatusSocket';
import API_ENDPOINTS from '../../config/apiConfig';

const RECONNECT_TOAST_ID = 'job-status-reconnecting';
const RECONNECT_FAILED_TOAST_ID = 'job-status-reconnect-failed';
const RECONNECT_TOAST_DELAY_MS = 1200; // avoid flashing a toast for brief blips
// With the hook's 1s/2s/4s/8s backoff, 5 failed attempts is ~15s of real
// elapsed time — enough for the "reconnecting" toast to actually be seen
// before escalating to the give-up message (3 attempts landed in ~3s, too
// fast to register).
const RECONNECT_ATTEMPTS_BEFORE_GIVING_UP = 5;
// Run states that can flip job-level fields (e.g. schedule.is_enabled for a
// one-time job) the job_status_update push doesn't carry — see applyJobUpdate.
const TERMINAL_RUN_STATUSES = ['SUCCESS', 'FAILED', 'CANCELLED'];

// Add or update a run within a job's runs array (by run id), keeping the list
// newest-first by run_number so `runs[0]` stays the latest. Handles the case
// where a scheduled/cron fire produces a brand-new run id we haven't seen.
const upsertRun = (runs = [], incoming) => {
    if (!incoming?.id) return runs;
    const exists = runs.some((r) => r.id === incoming.id);
    const next = exists
        ? runs.map((r) => (r.id === incoming.id ? { ...r, ...incoming } : r))
        : [incoming, ...runs];
    return [...next].sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0));
};

const Jobs = () => {
    const [jobs, setJobs] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [cloneSourceJob, setCloneSourceJob] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [pendingJobAction, setPendingJobAction] = useState(null);

    const KEY = 'jobsState';
    const parsedData = JSON.parse(sessionStorage.getItem(KEY) || '{}');

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(parsedData?.page || 1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchQuery, setSearchQuery] = useState(parsedData?.search || '');
    const itemsPerPage = 6;
    const debouncedSearch = useDebounce(searchQuery, 500);

    useEffect(() => {
        sessionStorage.setItem(KEY, JSON.stringify({ page: currentPage, search: searchQuery }));
    }, [currentPage, searchQuery]);


    const fetchJobs = useCallback(async () => {
        setIsLoading(true);
        try {

            await new Promise((resolve) => { setTimeout(resolve, 300) });

            const response = await jobService.getAllJobs({
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch
            });

            setJobs(response.items || []);
            setTotalItems(response.total || 0);

            if (currentPage > 1 && response.items.length === 0) {
                setCurrentPage(prev => prev - 1);
            }

        } catch (error) {
            console.error('Error fetching jobs:', error);
            toast.error(error.message || 'Failed to fetch jobs');
            setJobs([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearch]);

    // Fetch jobs on component mount and when pagination/search changes
    useEffect(() => {
        // setIsLoading(true);
        fetchJobs();
    }, [fetchJobs]);

    const handleToggle = (id) => {
        setJobs(jobs.map(j => (j.id === id ? { ...j, is_active: !j.is_active } : j)));
        // TODO: Add API call to update job active status
    };

    const handleAddJob = () => setIsAddModalOpen(true);
    const handleCloseAddModal = () => setIsAddModalOpen(false);

    const handleEditJob = (job) => {
        setSelectedJob(job);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedJob(null);
    };

    // --- Clone ---
    const handleCloneJob = (job) => {
        setCloneSourceJob(job);
        setIsCloneModalOpen(true);
    };

    const handleCloseCloneModal = () => {
        setIsCloneModalOpen(false);
        setCloneSourceJob(null);
    };

    // --- Delete ---
    const handleDeleteJob = (id) => {
        setPendingJobAction({ type: 'delete', id });
        setIsConfirmModalOpen(true);
    };

    const handleJobSuccess = async () => {
        // Refresh the jobs list after successful job creation
        await fetchJobs();
        setExpandedJobId(null);
    };

    const handleAddJobSuccess = async () => {
        if (currentPage === 1) {
            await fetchJobs();
        } else {
            setCurrentPage(1); // useEffect will trigger fetchJobs automatically
        }
        setExpandedJobId(null);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setExpandedJobId(null);
    };

    const handleSearchChange = (query) => {
        setSearchQuery(query);
        handlePageChange(1);
        // setCurrentPage(1);
        // setExpandedJobId(null);
    };

    const handleStartJob = (jobId, revert) => {
        setPendingJobAction({ type: 'start', id: jobId, revert });
        setIsConfirmModalOpen(true);
    };

    const handleStopJob = (jobId, revert) => {
        setPendingJobAction({ type: 'stop', id: jobId, revert });
        setIsConfirmModalOpen(true);
    };

    // const handleConfirmJobAction = useCallback(async () => {
    //     if (!pendingJobAction) return;

    //     setIsLoading(true);
    //     try {
    //         if (pendingJobAction.action === 'start') {
    //             setIsConfirmModalOpen(false);
    //             jobService.startJob(pendingJobAction.id)
    //                 .catch((error) => {
    //                     toast.error(error.message || 'Failed to start job. Please try again.');
    //                     console.error('Start job error:', error);
    //                 });
    //             await new Promise((resolve) => setTimeout(resolve, 5000));
    //             toast.success('Job started successfully');
    //             await fetchJobs();
    //         } else {
    //             setIsConfirmModalOpen(false);
    //             await jobService.stopJob(pendingJobAction.id);
    //             await fetchJobs();
    //             toast.success('Job stopped successfully');
    //         }
    //         // await fetchJobs();
    //     } catch (error) {
    //         toast.error(error.message || `Failed to ${pendingJobAction.action} job. Please try again.`);
    //         console.error(`${pendingJobAction.action} job error:`, error);
    //         // Revert the optimistic UI change on error
    //         pendingJobAction.revert?.();
    //         await fetchJobs();
    //     } finally {
    //         setIsLoading(false);
    //         // setIsConfirmModalOpen(false);
    //         setPendingJobAction(null);
    //     }
    // }, [pendingJobAction, fetchJobs]);

    const handleConfirmJobAction = useCallback(async () => {
        if (!pendingJobAction) return;

        setIsLoading(true);
        try {
            if (pendingJobAction.type === 'start') {
                setIsConfirmModalOpen(false);
                let jobFailed = false;

                const errorPromise = new Promise((resolve) => {
                    jobService.startJob(pendingJobAction.id)
                        .catch((error) => {
                            jobFailed = true;
                            toast.error(error.message || 'Failed to start job. Please try again.');
                            // console.error('Start job error:', error);
                            resolve(); // bail out of delay early on error
                        });
                });

                const delay = new Promise((resolve) => setTimeout(resolve, 5000));

                await Promise.race([delay, errorPromise]);
                if (!jobFailed) toast.success('Job started successfully');
                await fetchJobs();
            } else if (pendingJobAction.type === 'stop') {
                setIsConfirmModalOpen(false);
                const response = await jobService.stopJob(pendingJobAction.id);
                // await new Promise((resolve) => setTimeout(resolve, 3000));
                if (response) {
                    await fetchJobs();
                    toast.success('Job stopped successfully');
                }

            } else if (pendingJobAction.type === 'delete') {
                const response = await jobService.deleteJob(pendingJobAction.id);
                if (response) toast.success('Job deleted successfully');
                await fetchJobs();
            }
        } catch (error) {
            toast.error(error.message || `Failed to ${pendingJobAction.type} job. Please try again.`);
            // console.error(`${pendingJobAction.type} job error:`, error);
            pendingJobAction.revert?.();
            await fetchJobs();
        } finally {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            setPendingJobAction(null);
        }
    }, [pendingJobAction, fetchJobs]);

    const handleCancelJobAction = () => {
        // Revert the optimistic UI change when user cancels
        pendingJobAction?.revert?.();
        setIsConfirmModalOpen(false);
        setPendingJobAction(null);
    };

    const handleExpandToggle = (jobId) => {
        setExpandedJobId(prev => (prev === jobId ? null : jobId));
    };

    // Merge a single run object (from REST or a socket push) into state.
    const mergeRunIntoJobs = useCallback((updatedRun) => {
        if (!updatedRun?.id) return;
        setJobs(prev => prev.map(job => {
            if (!job.runs?.some(run => run.id === updatedRun.id)) return job;
            return {
                ...job,
                runs: job.runs.map(run =>
                    run.id === updatedRun.id ? { ...run, ...updatedRun } : run
                ),
            };
        }));
    }, []);

    // Pull the current page from the backend and reconcile it into state. Used
    // after a socket reconnect, where status changes may have been pushed while
    // we were disconnected (and won't be replayed). Job-level fields are taken
    // fresh; runs are unioned by id with fresh winning on overlap, so missed
    // status changes and newly created runs both land — without dropping any
    // local run the list endpoint happens not to return.
    const resyncJobs = useCallback(async () => {
        try {
            const response = await jobService.getAllJobs({
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch,
            });
            const freshJobById = new Map((response.items || []).map((j) => [j.id, j]));
            setJobs(prev => prev.map(job => {
                const fresh = freshJobById.get(job.id);
                if (!fresh) return job;
                const byId = new Map((job.runs || []).map((r) => [r.id, r]));
                (fresh.runs || []).forEach((r) =>
                    byId.set(r.id, { ...(byId.get(r.id) || {}), ...r })
                );
                const mergedRuns = [...byId.values()]
                    .sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0));
                return { ...job, ...fresh, runs: mergedRuns };
            }));
        } catch (syncError) {
            console.error('Job status resync error:', syncError);
        }
    }, [currentPage, itemsPerPage, debouncedSearch]);

    // ── Live status via WebSocket ─────────────────────────────────────────────

    // A pushed `job_status_update`. Merge by job_id: update the job-level status
    // and add/update the run it carries. Events for jobs not on the current page
    // fall through the .map untouched (client-side filtering, by design).
    //
    // The push payload only ever carries `status`/`run` — never job-level fields
    // like `schedule` (see useJobStatusSocket's documented contract). A run
    // reaching a terminal state can flip those server-side (e.g. a one-time
    // job's schedule.is_enabled once it's consumed), so when that happens for a
    // job we're currently showing, follow up with the same full resync used on
    // socket reconnect / manual refresh, instead of leaving it stale until then.
    const applyJobUpdate = useCallback((message) => {
        const { job_id: jobId, job_status: jobStatus, run, job_name: jobName } = message || {};
        if (!jobId) return;
        const jobOnPage = jobs.some((job) => job.id === jobId);

        // run === null: a scheduled run failed config validation before a run row
        // could be created. There's nothing to merge — update the job status and
        // surface the failure.
        if (run == null) {
            setJobs(prev => prev.map(job => (
                job.id === jobId ? { ...job, status: jobStatus ?? job.status } : job
            )));
            toast.error(`Scheduled run for "${jobName || 'a job'}" failed validation before it could start.`);
            if (jobOnPage) resyncJobs();
            return;
        }

        setJobs(prev => prev.map(job => {
            if (job.id !== jobId) return job;
            return {
                ...job,
                status: jobStatus ?? job.status,
                runs: upsertRun(job.runs, run),
            };
        }));

        if (jobOnPage && TERMINAL_RUN_STATUSES.includes(run.status)) {
            resyncJobs();
        }
    }, [jobs, resyncJobs]);

    const {
        socketState,
        reconnectAttempts,
    } = useJobStatusSocket({
        onJobUpdate: applyJobUpdate,
        // On reconnect we may have missed pushes — pull the current truth.
        onReconnected: resyncJobs,
        enabled: true,
    });

    // Surface "reconnecting" as a toast (only while a job is actually running)
    // instead of an inline pill. Delayed so a brief reconnect blip doesn't flash
    // a toast; dismissed the moment the socket is live or no job is active. If
    // retries keep failing, escalate to a "give up, refresh manually" toast.
    const reconnectToastTimerRef = useRef(null);
    useEffect(() => {
        const hasActiveRun = jobs.some((job) =>
            job.runs?.some((run) => run.status === 'RUNNING' || run.status === 'RETRYING')
        );

        if (socketState === SOCKET_STATE.RECONNECTING && hasActiveRun) {
            if (reconnectAttempts >= RECONNECT_ATTEMPTS_BEFORE_GIVING_UP) {
                if (reconnectToastTimerRef.current) {
                    clearTimeout(reconnectToastTimerRef.current);
                    reconnectToastTimerRef.current = null;
                }
                toast.dismiss(RECONNECT_TOAST_ID);
                if (!toast.isActive(RECONNECT_FAILED_TOAST_ID)) {
                    toast.error('Reconnection failed. Please refresh manually.', {
                        toastId: RECONNECT_FAILED_TOAST_ID,
                        autoClose: false,
                        closeOnClick: false,
                    });
                }
            } else if (!reconnectToastTimerRef.current && !toast.isActive(RECONNECT_TOAST_ID)) {
                reconnectToastTimerRef.current = setTimeout(() => {
                    toast.warning('Reconnecting', {
                        toastId: RECONNECT_TOAST_ID,
                        autoClose: false,
                        closeOnClick: false,
                    });
                    reconnectToastTimerRef.current = null;
                }, RECONNECT_TOAST_DELAY_MS);
            }
        } else {
            if (reconnectToastTimerRef.current) {
                clearTimeout(reconnectToastTimerRef.current);
                reconnectToastTimerRef.current = null;
            }
            toast.dismiss(RECONNECT_TOAST_ID);
            toast.dismiss(RECONNECT_FAILED_TOAST_ID);
        }
    }, [socketState, jobs, reconnectAttempts]);

    useEffect(() => () => {
        if (reconnectToastTimerRef.current) clearTimeout(reconnectToastTimerRef.current);
    }, []);

    // Manual "Refresh Status" button. The live socket is receive-only, so this is
    // a plain REST pull for the single run the user asked to refresh.
    const handleRefreshRun = async (jobId, runId) => {
        try {
            const updatedRun = await jobService.getRunById(runId);
            mergeRunIntoJobs(updatedRun);
            // getRunById only returns run-level fields — a completed one-time run
            // can also flip job-level fields (e.g. schedule.is_enabled) that this
            // can't see, so pull those in too (same call used on socket reconnect).
            await resyncJobs();
            toast.success("Job refreshed successfully");
        } catch (error) {
            toast.error(error.message || 'Failed to refresh run status');
            console.error('Refresh run error:', error);
        }
    };

    const handleDownloadReport = async (type, runId, runLabel) => {
        // setIsLoading(true);
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await fetch(API_ENDPOINTS.JOBS.DOWNLOAD_REPORT(runId), {
                headers: {
                    'x-user-timezone': timezone
                }
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData?.detail || errData?.message || 'Failed to download report');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${runLabel || runId}_report.sql`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Report downloaded successfully');
        } catch (error) {
            toast.error(error.message || 'Failed to download report. Please try again.');
            console.error('Download report error:', error);
        } finally {
            // setIsLoading(false);
        }
    };

    const confirmMessage = pendingJobAction?.type === 'delete'
        ? 'Do you want to delete this job?'
        : 'Do you want to proceed?';

    return (
        <div className="page-content">
            {/* Loading Backdrop */}
            <LoadingBackdrop isLoading={isLoading} />

            <div className="page-header">
                <div className="page-title-section">
                    <h1 className="page-title">Jobs</h1>
                    <p className="page-subtitle">Browse and manage all jobs</p>
                </div>
                <button className="add-connection-btn" onClick={handleAddJob}>
                    <Plus style={{ width: "1.042vw", height: "1.042vw" }} />
                    Add New Job
                </button>
            </div>

            <EntityList
                items={jobs}
                title="Jobs"
                searchPlaceholder="Search jobs..."
                entityType="jobs"
                itemsPerPage={itemsPerPage}
                itemName="jobs"
                // Server-side pagination
                currentPage={currentPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onToggle={handleToggle}
                onAdd={handleAddJob}
                onEdit={handleEditJob}
                onClone={handleCloneJob}
                onDelete={handleDeleteJob}
                onStart={handleStartJob}
                onStop={handleStopJob}
                onDownloadReport={handleDownloadReport}
                expandedJobId={expandedJobId}
                onExpandToggle={handleExpandToggle}
                onRefreshRun={handleRefreshRun}
                setIsLoading={setIsLoading}
            />

            {isEditModalOpen && (
                <EditJobModal
                    isOpen={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    onSuccess={handleJobSuccess}
                    jobData={selectedJob}
                    setIsLoading={setIsLoading}
                />
            )}

            {isAddModalOpen && (
                <AddJobModal
                    isOpen={isAddModalOpen}
                    onClose={handleCloseAddModal}
                    onSuccess={handleAddJobSuccess}
                    setIsLoading={setIsLoading}
                />
            )}

            {/* Clone Job Modal */}
            {isCloneModalOpen && (
                <CloneJobModal
                    isOpen={isCloneModalOpen}
                    onClose={handleCloseCloneModal}
                    onSuccess={handleAddJobSuccess}
                    setIsLoading={setIsLoading}
                    jobData={cloneSourceJob}
                />
            )}

            {/* Confirmation Modal for Start/Stop */}
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCancelJobAction}
                onConfirm={handleConfirmJobAction}
                title="Confirm Action"
                message={confirmMessage}
                confirmText="Confirm"
                cancelText="Cancel"
                isLoading={isLoading}
            />
        </div>
    );
};

export default Jobs;